import type { AllEntities, API, BaseEntity, ListSearchResponse } from 'c-sdk';
import type { AxiosResponse } from 'axios';
import type { CoreThunk } from '../../Types/CoreThunk';
import type { SearchPayload } from '../../Types/Search';
import { getBaseSliceSelectors } from '../../Data/EntitySchemaActions';
import { normaliseDataThunks } from '../../Data/data-thunks';
import type { FakeApi } from '../../lib';
import {
    callApi,
    callFakeApiCreate,
    callFakeApiDeleteById,
    callFakeApiGetById,
    callFakeApiGetPaginatedList,
    callFakeApiUpdateById,
    errorMessageFromResponse,
    validationErrorsFromResponse,
} from '../../lib';
import type { BaseCaseReducers, BaseSliceInitialState, EntitySchemaType } from '../../Types';
import { NetworkRequestState } from '../../Types';
import type { BaseSliceDefaultThunks } from '../../Types/thunks';

/**
 * Monotonic request numbers, one run per entity+tag.
 *
 * Module level rather than in the store, because `resetPaginatedList` deletes `pagination[tag]`
 * outright — `useCursorPaginatedEntityData.getFirst` does exactly that before every first page. A
 * counter kept in there would restart at zero while an older request was still in flight, and the
 * stale response would then look like the newest.
 */
const requestSequences = new Map<string, number>();

const nextRequestSequence = (listKey: string) => {
    const next = (requestSequences.get(listKey) ?? 0) + 1;
    requestSequences.set(listKey, next);

    return next;
};

/**
 * The payload of the request currently in flight for each list, if any.
 *
 * This is a de-duplicator, not a concurrency guard: a request whose payload differs from the one in
 * flight always proceeds, which is what a user typing or changing a filter produces. Only a
 * *byte-identical* repeat is skipped.
 *
 * It has to exist because several callers ask on every render — `AllEntitiesDropdown` calls
 * `useAllEntityData.getAll` during render — and without something to
 * absorb that, the same request is issued in a loop. The blanket in-flight check that used to live
 * here stopped those loops too, but it did so by discarding *any* overlapping request, which is how
 * user input went missing.
 */
const inFlightPayloads = new Map<string, string>();

const baseThunks =
    <RootState>(entitySchemas: EntitySchemaType, apiClient: API) =>
    <Entity extends BaseEntity>(
        entityName: keyof AllEntities,
        actions: BaseCaseReducers<Entity, BaseSliceInitialState<Entity>, any>,
        fakeApi?: FakeApi<Entity>,
    ): BaseSliceDefaultThunks<RootState, Entity> => {
        if (process.env.NODE_ENV === 'development') {
            if (typeof apiClient.Entities[entityName]?.get !== 'function') {
                // eslint-disable-next-line no-console
                console.warn(`'get' method missing for ${entityName} in 'ts-api-sdk'`);
            }
            if (typeof apiClient.Entities[entityName]?.update !== 'function') {
                // eslint-disable-next-line no-console
                console.warn(`'update' method missing for ${entityName} in 'ts-api-sdk'`);
            }
            if (typeof apiClient.Entities[entityName]?.create !== 'function') {
                // eslint-disable-next-line no-console
                console.warn(`'create' method missing for ${entityName} in 'ts-api-sdk'`);
            }
        }

        return {
            resetLoadingByIdState:
                (id: number): CoreThunk<RootState> =>
                async dispatch => {
                    dispatch(actions.resetLoadingByIdState({ id }));
                },
            resetUpdatingByIdState:
                (id: number): CoreThunk<RootState> =>
                async dispatch => {
                    dispatch(actions.resetUpdatingByIdState({ id }));
                },
            resetCreateState: (): CoreThunk<RootState> => async dispatch => {
                dispatch(actions.resetCreateState());
            },
            resetDeletingByIdState:
                (id: number): CoreThunk<RootState> =>
                async dispatch => {
                    dispatch(actions.resetDeletingByIdState({ id }));
                },
            getById:
                (id: number, includes?: string[]): CoreThunk<RootState> =>
                async (dispatch, getState) => {
                    if (!fakeApi?.useFake && apiClient.Entities[entityName]?.get == null) {
                        // Not all entities have their own get endpoint because they are received
                        // as children of other entities and stored through the normalisation process
                        // soooo preventing any fetching being done for those entities which don't
                        // have endpoints configured
                        return;
                    }
                    if (id === 0) {
                        return;
                    }
                    const selectors = getBaseSliceSelectors<Entity>(entityName);
                    if (
                        selectors.loadingByIdState(getState(), id) ===
                        NetworkRequestState.InProgress
                    ) {
                        return;
                    }

                    dispatch(actions.startLoadingById({ id }));

                    const [error, data, response] = await (fakeApi?.useFake
                        ? callFakeApiGetById<Entity>({
                              id,
                              entityName,
                              ...fakeApi,
                          })
                        : dispatch(
                              callApi(
                                  apiClient.Entities[entityName].get(id, includes)
                                      .request as unknown as Promise<
                                      AxiosResponse<{ data: Entity }>
                                  >,
                              ),
                          ));
                    const status = response?.status ?? error?.response?.status;
                    if (data) {
                        dispatch(
                            normaliseDataThunks.updateWithNormalisedData(
                                Array.isArray(data.data)
                                    ? [entitySchemas[entityName]]
                                    : entitySchemas[entityName],
                                data.data,
                            ),
                        );
                        dispatch(actions.finishedLoadingById({ entity: data.data, status }));
                        return;
                    }

                    dispatch(
                        actions.failedLoadingById({
                            id,
                            error: errorMessageFromResponse(error),
                            status,
                        }),
                    );
                },
            getPaginatedList:
                (
                    tag: string,
                    searchPayload: SearchPayload<Entity>,
                    cursorPaginated,
                    customListFunction,
                ): CoreThunk<RootState> =>
                async dispatch => {
                    const listKey = `${entityName}:${tag}`;
                    const payloadKey = JSON.stringify(searchPayload ?? {});

                    // Skip only an identical repeat of what is already running. Anything with a
                    // different payload goes through and, if it finishes out of order, the reducers
                    // discard whichever response is no longer the newest.
                    if (inFlightPayloads.get(listKey) === payloadKey) {
                        return;
                    }

                    const requestSequence = nextRequestSequence(listKey);
                    inFlightPayloads.set(listKey, payloadKey);

                    dispatch(
                        actions.startLoadingPaginatedList({
                            ...searchPayload,
                            tag,
                            requestSequence,
                        }),
                    );

                    const listFunction = () => {
                        if (fakeApi?.useFake) {
                            return callFakeApiGetPaginatedList<Entity>({
                                page: searchPayload.page,
                                searchPayload,
                                entityName,
                                ...fakeApi,
                            });
                        }

                        if (customListFunction) {
                            return dispatch(callApi(customListFunction(searchPayload).request));
                        }

                        if (cursorPaginated) {
                            return dispatch(
                                callApi(
                                    apiClient.Entities[entityName].cursor(searchPayload)
                                        .request as unknown as Promise<
                                        AxiosResponse<ListSearchResponse<Entity>>
                                    >,
                                ),
                            );
                        }

                        return dispatch(
                            callApi(
                                apiClient.Entities[entityName].list(searchPayload)
                                    .request as unknown as Promise<
                                    AxiosResponse<ListSearchResponse<Entity>>
                                >,
                            ),
                        );
                    };

                    const [error, success] = await listFunction();

                    if (inFlightPayloads.get(listKey) === payloadKey) {
                        inFlightPayloads.delete(listKey);
                    }

                    // `success.data` is the rows. A body arriving without it is not something the
                    // reducer or the normaliser can read, and throwing here takes the whole page
                    // down — so treat it as a failure rather than trusting the shape.
                    if (!error && success?.data != null) {
                        dispatch(
                            normaliseDataThunks.updateWithNormalisedData(
                                [entitySchemas[entityName]],
                                success.data,
                            ),
                        );
                        dispatch(
                            actions.finishedLoadingPaginatedList({
                                ...success,
                                tag,
                                wasCursorRequest: cursorPaginated,
                                requestSequence,
                            }),
                        );
                        return;
                    }

                    dispatch(
                        actions.failedLoadingPaginatedList({
                            error: errorMessageFromResponse(error),
                            tag,
                            requestSequence,
                        }),
                    );
                },
            resetPaginatedList:
                (tag: string): CoreThunk<RootState> =>
                async dispatch => {
                    dispatch(actions.resetPaginatedList({ tag }));
                },
            resetPaginatedListFilters: (tag, value) => async dispatch => {
                dispatch(actions.resetPaginatedListFilters({ tag, value }));
            },
            updatePaginatedListFilter: (tag, key, value) => async dispatch => {
                dispatch(actions.updatePaginatedListFilter({ tag, key, value }));
            },
            togglePaginatedListFilterValue: (tag, key, value) => async (dispatch, getState) => {
                dispatch(actions.togglePaginatedListFilterValue({ tag, key, value }));
            },
            updateById:
                (id: number, data: Partial<Entity>, includes?: string[]): CoreThunk<RootState> =>
                async (dispatch, getState) => {
                    const selectors = getBaseSliceSelectors<Entity>(entityName);
                    if (
                        selectors.updatingByIdState(getState(), id) ===
                        NetworkRequestState.InProgress
                    ) {
                        return;
                    }

                    dispatch(actions.startUpdatingById({ id }));

                    const [error, success] = await (fakeApi?.useFake
                        ? callFakeApiUpdateById<Entity>({
                              id,
                              data,
                              entityName,
                              ...fakeApi,
                          })
                        : dispatch(
                              callApi(
                                  apiClient.Entities[entityName].update(
                                      id as any,
                                      data as any,
                                      includes,
                                  ).request as unknown as Promise<AxiosResponse<{ data: Entity }>>,
                              ),
                          ));

                    if (success) {
                        dispatch(
                            normaliseDataThunks.updateWithNormalisedData(
                                entitySchemas[entityName],
                                success.data,
                            ),
                        );
                        dispatch(actions.finishedUpdatingById(success.data));
                        return;
                    }

                    dispatch(
                        actions.failedUpdatingById({
                            id,
                            error: errorMessageFromResponse(error),
                            validation: validationErrorsFromResponse(error),
                        }),
                    );
                },
            create:
                (entity: Partial<Entity>, includes?: string[]): CoreThunk<RootState> =>
                async (dispatch, getState: () => RootState) => {
                    const selectors = getBaseSliceSelectors<Entity>(entityName);
                    // BaseSliceSelectors types creatingState as OutputSelector<any, ...>, which
                    // reselect 4 reads as taking `never`; it is a plain (state) => state selector.
                    const creatingState = selectors.creatingState as unknown as (
                        state: RootState,
                    ) => NetworkRequestState;
                    if (creatingState(getState()) === NetworkRequestState.InProgress) {
                        return;
                    }

                    dispatch(actions.startCreating());

                    const [error, success] = await (fakeApi?.useFake
                        ? callFakeApiCreate<Entity>({
                              entity,
                              entityName,
                              ...fakeApi,
                          })
                        : dispatch(
                              callApi(
                                  apiClient.Entities[entityName].create(entity as any, includes)
                                      .request as unknown as Promise<
                                      AxiosResponse<{ data: Entity }>
                                  >,
                              ),
                          ));

                    if (success) {
                        dispatch(
                            normaliseDataThunks.updateWithNormalisedData(
                                entitySchemas[entityName],
                                success.data,
                            ),
                        );
                        dispatch(actions.finishedCreating(success.data));
                        return;
                    }

                    dispatch(
                        actions.failedCreating({
                            error: errorMessageFromResponse(error),
                            validation: validationErrorsFromResponse(error),
                        }),
                    );
                },
            deleteById:
                (id: number): CoreThunk<RootState> =>
                async (dispatch, getState) => {
                    const selectors = getBaseSliceSelectors<Entity>(entityName);
                    if (
                        selectors.deletingByIdState(getState(), id) ===
                        NetworkRequestState.InProgress
                    ) {
                        return;
                    }

                    dispatch(actions.startDeletingById({ id }));

                    const [error, success] = await (fakeApi?.useFake
                        ? callFakeApiDeleteById<Entity>({
                              id,
                              entityName,
                              ...fakeApi,
                          })
                        : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          dispatch(callApi(apiClient.Entities[entityName].delete(id).request)));

                    if (success) {
                        dispatch(actions.finishedDeletingById({ id }));
                        return;
                    }

                    dispatch(
                        actions.failedDeletingById({
                            id,
                            error: errorMessageFromResponse(error),
                        }),
                    );
                },
            upsertEntities:
                (entities): CoreThunk<RootState> =>
                async (dispatch, getState) => {
                    dispatch(
                        normaliseDataThunks.updateWithNormalisedData(
                            [entitySchemas[entityName]],
                            entities,
                        ),
                    );
                },
            upsertEntity:
                (entity): CoreThunk<RootState> =>
                async (dispatch, getState) => {
                    dispatch(
                        normaliseDataThunks.updateWithNormalisedData(
                            entitySchemas[entityName],
                            entity,
                        ),
                    );
                },
        };
    };

export default baseThunks;
